"use client";

import { useState, useEffect, useRef } from "react";
import { Globe } from "lucide-react";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
/* 전 locale prefix(ko|en|ja|zh|th) 제거 후 새 locale 붙임 — 공용 규칙.
   한국어 /main/X 화면(예: /main/myTickets)에서 외국어로 바꿀 때 /en/main/X(404)가 아니라 /en/X 로 간다. */
import { withLocaleHref } from "@kkeut/lib/locale-url";

const LABEL = { ko: "한국어", en: "English", ja: "日本語", zh: "中文", th: "ภาษาไทย" };

/**
 * @param {object} props
 * @param {string[]} props.locales   - 지원 locale 목록 (LOCALES from @/lib/locale)
 * @param {string}   props.locale    - 현재 locale
 * @param {Function} props.setLocale - locale 상태 setter
 * @param {Function} [props.switchLocale] - 라우팅 기반 전환 hook fn (optional)
 * @param {string}   [props.className]
 */
export function LanguageSwitcher({ locales, locale, setLocale, switchLocale, className = "" }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [supportsHover, setSupportsHover] = useState(false);
  const closeTimer = useRef(null);

  useEffect(() => {
    const m = window.matchMedia?.("(hover: hover)");
    const update = () => setSupportsHover(!!m?.matches);
    update();
    m?.addEventListener?.("change", update);
    return () => m?.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const close = () => setOpen(false);
  const scheduleClose = () => {
    if (!supportsHover) return;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  };
  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  const handleButtonClick = () => {
    if (supportsHover) { cancelClose(); setOpen(true); }
    else setOpen((v) => !v);
  };

  const switchTo = (next) => {
    setOpen(false);
    if (next === locale) return;
    setLocale(next);
    try {
      if (typeof switchLocale === "function") { switchLocale(next); return; }
    } catch {}
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    const current = pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : "") + (hash || "");
    router.replace(withLocaleHref(current, next));
  };

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={supportsHover ? () => { cancelClose(); setOpen(true); } : undefined}
      onMouseLeave={supportsHover ? scheduleClose : undefined}
    >
      {!supportsHover && open && (
        <button aria-hidden className="fixed inset-0 z-40 bg-transparent" onClick={close} />
      )}

      <button
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Change language"
        onClick={handleButtonClick}
        className="p-1 rounded-full focus:outline-none focus:ring"
      >
        <Globe className="w-5 h-5" />
      </button>

      {supportsHover && open && (
        <div
          className="absolute left-0 right-0 top-full h-3 z-50"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          style={{ pointerEvents: "auto" }}
        />
      )}

      <div
        onMouseEnter={supportsHover ? cancelClose : undefined}
        onMouseLeave={supportsHover ? scheduleClose : undefined}
        className={`absolute right-0 top-full mt-2 w-40 rounded-xl border bg-white text-black shadow z-50
          text-base font-normal tracking-normal leading-6 transition-opacity duration-150
          ${open ? "visible opacity-100 pointer-events-auto" : "invisible opacity-0 pointer-events-none"}`}
      >
        <div className="px-3 py-2 text-xs text-gray-500">Language</div>
        <ul role="menu" className="py-1">
          {(locales || []).filter((l) => l !== locale).map((l) => (
            <li key={l}>
              <button
                role="menuitem"
                onClick={() => switchTo(l)}
                className="w-full text-left px-3 py-2 text-black font-normal hover:bg-gray-50 hover:text-gray-900"
              >
                {LABEL[l] ?? l}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
